import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle, User, Building, MessageSquare } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";
import SEOHead from "../components/SEOHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    entreprise: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSuccess(true);
      setFormData({ prenom: "", nom: "", email: "", telephone: "", entreprise: "", message: "" });
    } catch (err) {
      setError(t('contactPage.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const testimonials = [
    {
      quote: language === 'fr' 
        ? "ImpaKt Studio a transformé notre image de marque. Nous sommes passés d'une startup invisible à une référence dans notre secteur. Leur approche stratégique et créative est exceptionnelle."
        : "ImpaKt Studio transformed our brand image. We went from an invisible startup to a benchmark in our sector. Their strategic and creative approach is exceptional.",
      author: "Sophie Martin",
      role: language === 'fr' ? "CEO, TechVision" : "CEO, TechVision",
      company: "TechVision",
      initials: "SM"
    },
    {
      quote: language === 'fr'
        ? "Une équipe à l'écoute, créative et professionnelle. Le Brand Impact Program nous a permis de clarifier notre positionnement et de multiplier par 3 notre taux de conversion."
        : "A team that listens, creative and professional. The Brand Impact Program allowed us to clarify our positioning and triple our conversion rate.",
      author: "Jean Dupont",
      role: language === 'fr' ? "Fondateur, InnovCorp" : "Founder, InnovCorp",
      company: "InnovCorp",
      initials: "JD"
    },
    {
      quote: language === 'fr'
        ? "Collaboration exceptionnelle ! L'équipe a su comprendre notre vision et la traduire en une identité visuelle forte qui nous différencie vraiment de nos concurrents."
        : "Exceptional collaboration! The team understood our vision and translated it into a strong visual identity that truly differentiates us from our competitors.",
      author: "Marie Laurent",
      role: language === 'fr' ? "Directrice Marketing, Élégance & Co" : "Marketing Director, Élégance & Co",
      company: "Élégance & Co",
      initials: "ML"
    }
  ];

  const stats = [
    { value: "15+", label: t('stats.years') },
    { value: "150+", label: t('stats.projects') },
    { value: "3x", label: t('stats.growth') },
    { value: "100%", label: t('stats.satisfaction') },
  ];

  return (
    <div className="pt-20">
      <SEOHead 
        title={language === 'fr' 
          ? "Contact | ImpaKt Studio - Parlons de votre projet" 
          : "Contact | ImpaKt Studio - Let's discuss your project"
        }
        description={language === 'fr'
          ? "Contactez ImpaKt Studio pour discuter de votre projet de branding et stratégie digitale. Réservez un appel stratégique gratuit."
          : "Contact ImpaKt Studio to discuss your branding and digital strategy project. Book a free strategic call."
        }
        path="/contact"
      />
      
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-cyan-500 to-cyan-600" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-cyan-100 font-medium text-sm uppercase tracking-wider">{t('nav.contact')}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            {t('contactPage.heroTitle')}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {t('contactPage.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <div>
              <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">{t('nav.contact')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
                <span className="whitespace-nowrap">{t('contactPage.formTitle')}</span>
              </h2>
              <p className="text-gray-600 text-lg mb-10">
                {t('contactPage.formSubtitle')}
              </p>

              <div className="mb-10">
                <h3 className="font-semibold text-gray-900 mb-6">{t('contactPage.contactDirectly')}</h3>
                <p className="text-gray-600 mb-6">
                  {t('contactPage.contactDirectlyDesc')}
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center text-cyan-600">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('contact.info.email')}</p>
                      <a href="mailto:contact@impakt-studio.com" className="text-gray-900 font-medium hover:text-cyan-600 transition-colors">
                        contact@impakt-studio.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center text-cyan-600">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('contact.info.phone')}</p>
                      <a href="tel:+33975953796" className="text-gray-900 font-medium hover:text-cyan-600 transition-colors">
                        +33 9 75 95 37 96
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center text-cyan-600">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('contactPage.address')}</p>
                      <p className="text-gray-900 font-medium">
                        Lyon, Paris, Genève, Bruxelles, Nashville, Kigali, Bangui, Cotonou
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80"
                alt="Success"
                className="rounded-2xl shadow-lg w-full"
              />
            </div>

            {/* Right - Form */}
            <div className="bg-gray-50 rounded-2xl p-8 md:p-10">
              {success ? (
                <div className="text-center py-12" data-testid="success-message">
                  <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('contactPage.messageSent')}</h3>
                  <p className="text-gray-600 mb-6">
                    {t('contactPage.messageSuccess')}
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="text-cyan-600 font-semibold hover:underline"
                  >
                    {t('contactPage.sendAnother')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {t('contact.form.firstName')} <span className="text-red-500">*</span>
                        </span>
                      </label>
                      <input
                        type="text"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                        data-testid="input-prenom"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.form.lastName')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                        data-testid="input-nom"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {t('contact.form.email')} <span className="text-red-500">*</span>
                      </span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                      data-testid="input-email"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {t('contact.form.phone')}
                        </span>
                      </label>
                      <input
                        type="tel"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                        data-testid="input-telephone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <Building className="w-4 h-4" />
                          {t('contact.form.company')}
                        </span>
                      </label>
                      <input
                        type="text"
                        name="entreprise"
                        value={formData.entreprise}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                        data-testid="input-entreprise"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        {t('contact.form.message')} <span className="text-red-500">*</span>
                      </span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all resize-none"
                      data-testid="input-message"
                    />
                  </div>

                  {error && (
                    <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg" data-testid="error-message">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full mt-8 px-8 py-4 bg-cyan-500 text-white font-semibold rounded-full hover:bg-cyan-400 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t('contact.form.sending')}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t('contact.form.send')}
                      </>
                    )}
                  </button>

                  <p className="mt-4 text-sm text-gray-500 text-center">
                    {t('contactPage.submitDisclaimer')}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">{t('contactPage.testimonialsTitle')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
              {t('contactPage.testimonialsSubtitle')}
            </h2>
            <p className="text-gray-600">
              {t('contactPage.testimonialsDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg"
                data-testid={`testimonial-${index}`}
              >
                <p className="text-gray-600 italic mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600 font-bold">
                    {testimonial.initials}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gradient-to-br from-cyan-500 to-cyan-600" data-testid="contact-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white">{stat.value}</div>
                <div className="text-cyan-100 mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
